import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useResolvedPath } from 'react-router-dom';
import { useAppointmentState, useEnsureAppointment, useTowInProcess } from '../../../context/Appointment/appointment';
import { Types, useCartDispatch } from '../../../context/cart';
import { useLocale, useLocalization } from '../../../context/Localization/localization';
import { KioskAwareControl } from '../../../controls/AccessControl/KioskAwareControl';
import { CTAButton } from '../../../controls/ButtonBase';
import { Heading } from '../../../controls/Heading/Heading';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { Mileage } from '../../Appointment/Mileage';
import { ScrollableBody } from '../../Layout/ScrollableBody/ScrollableBody';
import { ConfirmInformation } from './ConfirmInformation';
import { InformationForm } from './InformationForm';
import { LicenseScanIndex } from './LicenseScan/LicenseScanIndex';
import { Vehicle } from './Vehicle';
import { TagNumber } from '../../Appointment/TagNumber';
import { useIsHardwareError } from '../../../context/hardware';
import { useKioskState, useTowInSupport } from '../../../context/Kiosk/kiosk';
import { getIsInformationMissing } from '../../../helpers/Appointment';
import { AppRoutePaths, AppointmentRoutePaths, CustomerRoutePaths, Customer_AddRoutesPaths, PathName } from '../../../helpers/Routes';
import { AppointmentRoute } from '../../Appointment/AppointmentRoute';
import { TowCompanyDetails } from '../../Appointment/TowCompanyDetails';
import { VinBarcodeScan } from './Vehicle/VinBarcodeScan/VinBarcodeScan';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { Locales } from '../../../context/Localization/localization.types';
import { CustomerDetails, TowInDetails } from '../../Appointment/Details';

export const AddIndex = React.memo(() => {
    const navigate = useNavigate();
    const cartDispatch = useCartDispatch();
    const { isLoading } = useEnsureAppointment();

    useEffect(() => {
        cartDispatch({
            type: Types.DisplayCartIcon,
            payload: false,
        });
    });

    const onProceed = async () => {
        navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Valuation));
    };

    const onSkip = () => navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Valuation), { replace: true });

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <KioskAwareControl
            kioskComponent={
                <KioskRoutes
                    {...{
                        onSkip,
                        onProceed,
                    }}
                />
            }
            webComponent={
                <WebRoutes
                    {...{
                        onSkip,
                        onProceed,
                    }}
                />
            }
        />
    );
});

interface IProps {
    onProceed: () => Promise<void>;
    onSkip: () => void;
}

const WebRoutes = (props: IProps) => {
    const { pathname } = useResolvedPath('.');
    const {
        dealer: { newCustomerEnabled = true },
    } = useKioskState();
    const towInProcess = useTowInProcess();
    const navigate = useNavigate();
    const appointment = useAppointmentState();
    const isInformationMissing = getIsInformationMissing(appointment);
    const towInSupport = useTowInSupport();

    return (
        <Routes>
            {newCustomerEnabled && (
                <Route
                    path={''}
                    element={
                        <AppointmentRoute>
                            <InformationForm onProceed={() => navigate(PathName(pathname, Customer_AddRoutesPaths.Details))} />
                        </AppointmentRoute>
                    }
                />
            )}
            {
                towInSupport &&
                <Route
                    path={PathName(Customer_AddRoutesPaths.TowCompanyDetails, '*')}
                    element={
                        <AppointmentRoute>
                            <TowCompanyDetails
                                onProceed={() => {
                                    navigate(PathName(pathname, Customer_AddRoutesPaths.Details));
                                }}
                            />
                        </AppointmentRoute>
                    }
                />
            }
            {(!getIsInformationMissing(appointment) || towInProcess) && (
                <Route
                    path={PathName(Customer_AddRoutesPaths.Details, '*')}
                    element={
                        <AppointmentRoute>
                            {towInProcess ? (
                                <TowInDetails
                                    onProceed={() => {
                                        navigate(PathName(pathname, Customer_AddRoutesPaths.Vehicle));
                                    }}
                                />

                            ) : (
                                <CustomerDetails
                                    onProceed={() => {
                                        if (!('dmsCustomerKey' in appointment)) {
                                            navigate(PathName(pathname, Customer_AddRoutesPaths.Confirm));
                                        }
                                        else {
                                            navigate(PathName(pathname, Customer_AddRoutesPaths.Vehicle));
                                        }
                                    }}
                                />
                            )}
                        </AppointmentRoute>
                    }
                />
            )}
            <Route
                path={PathName(Customer_AddRoutesPaths.Confirm, '*')}
                element={
                    <AppointmentRoute>
                        {newCustomerEnabled ? (
                            <ConfirmInformation
                                onInformationCorrect={() => {
                                    if (appointment.mobileCheckInComplete) {
                                        navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Mileage), { replace: true });
                                    }
                                    else {
                                        navigate(PathName(pathname, Customer_AddRoutesPaths.Vehicle), { replace: true });
                                    }
                                }}
                                onInformationIncorrect={() => navigate(pathname)}
                                onExistingCustomer={() => navigate(PathName(AppRoutePaths.Customer, CustomerRoutePaths.Lookup))}
                                onInformationMissing={() => navigate('/')}
                            />
                        ) : (
                            <Navigate replace to={PathName(pathname, Customer_AddRoutesPaths.Vehicle)} />
                        )}
                    </AppointmentRoute>
                }
            />
            {(!isInformationMissing || towInProcess) && (
                <Route
                    path={PathName(Customer_AddRoutesPaths.Vehicle, '*')}
                    element={
                        <AppointmentRoute>
                            <Vehicle
                                onProceed={() => {
                                    if (towInProcess) {
                                        navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.TowInAdditionalDetails));
                                    }
                                    else {
                                        navigate(PathName(pathname, Customer_AddRoutesPaths.Mileage));
                                    }
                                }}
                            />
                        </AppointmentRoute>
                    }
                />
            )}
            {(!isInformationMissing || towInProcess) && (
                <Route
                    path={PathName(Customer_AddRoutesPaths.VinScanMobile, '*')}
                    element={
                        <AppointmentRoute>
                            <VinBarcodeScan
                                onScan={() => navigate(PathName(pathname, AppointmentRoutePaths.Vehicle))}
                                onCancel={() => navigate(-1)}
                            />
                        </AppointmentRoute>
                    }
                />
            )}
            {!isInformationMissing && (
                <Route
                    path={PathName(Customer_AddRoutesPaths.Mileage, '*')}
                    element={
                        <AppointmentRoute>
                            <Mileage onProceed={() => navigate(PathName(pathname, Customer_AddRoutesPaths.Tagnumber))} />
                        </AppointmentRoute>
                    }
                />
            )}
            {!isInformationMissing && (
                <Route
                    path={PathName(Customer_AddRoutesPaths.Tagnumber, '*')}
                    element={
                        <AppointmentRoute>
                            <TagNumber onProceed={props.onProceed} onSkip={props.onSkip} />
                        </AppointmentRoute>
                    }
                />
            )}
            <Route path={'*'} element={<Navigate replace to={'/'} />} />
        </Routes>
    );
};

const KioskRoutes = (props: IProps) => {
    const { pathname } = useResolvedPath('.');
    const navigate = useNavigate();
    const isError = useIsHardwareError();
    const location = useLocation();
    const Localization = useLocalization();
    const currentLocale = useLocale();
    const {
        dealer: { newCustomerEnabled = true },
    } = useKioskState();
    const towInProcess = useTowInProcess();
    const appointment = useAppointmentState();
    const towInSupport = useTowInSupport();

    React.useEffect(() => {
        if (location.pathname === pathname && isError()) {
            navigate(pathname, { replace: true });
        }
    });

    return (
        <Routes>
            <Route
                path={PathName(Customer_AddRoutesPaths.Scan, '*')}
                element={
                    <LicenseScanIndex
                        onManualEntry={() => navigate(PathName(pathname, Customer_AddRoutesPaths.CustomerInfo))}
                        onProceed={() => {
                            navigate(PathName(pathname, Customer_AddRoutesPaths.Details));
                        }}
                    />
                }
            />
            <Route
                path={Customer_AddRoutesPaths.CustomerInfo}
                element={
                    <AppointmentRoute>
                        <InformationForm onProceed={() => navigate(PathName(pathname, Customer_AddRoutesPaths.Details))} />
                    </AppointmentRoute>
                }
            />
            {
                towInSupport &&
                <Route
                    path={PathName(Customer_AddRoutesPaths.TowCompanyDetails, '*')}
                    element={
                        <AppointmentRoute>
                            <TowCompanyDetails
                                onProceed={() => {
                                    navigate(PathName(pathname, Customer_AddRoutesPaths.Details));
                                }}
                            />
                        </AppointmentRoute>
                    }
                />
            }
            <Route
                path={PathName(Customer_AddRoutesPaths.Details, '*')}
                element={
                    <AppointmentRoute>
                        {towInProcess ? (
                            <TowInDetails
                                onProceed={() => {
                                    navigate(PathName(pathname, Customer_AddRoutesPaths.Vehicle));
                                }}
                            />
                        ) : (
                            <CustomerDetails
                                onProceed={() => {
                                    if (!('dmsCustomerKey' in appointment)) {
                                        navigate(PathName(pathname, Customer_AddRoutesPaths.Confirm));
                                    }
                                    else {
                                        navigate(PathName(pathname, Customer_AddRoutesPaths.Vehicle));
                                    }
                                }}
                            />
                        )}
                    </AppointmentRoute>
                }
            />
            <Route
                path={PathName(Customer_AddRoutesPaths.Confirm, '*')}
                element={
                    <AppointmentRoute>
                        {newCustomerEnabled ? (
                            <ConfirmInformation
                                onInformationCorrect={() => navigate(PathName(pathname, Customer_AddRoutesPaths.Vehicle))}
                                onInformationIncorrect={() => navigate(PathName(pathname, Customer_AddRoutesPaths.CustomerInfo))}
                                onExistingCustomer={() => navigate(PathName(AppRoutePaths.Customer, CustomerRoutePaths.Lookup))}
                                onInformationMissing={() => navigate('/')}
                            />
                        ) : (
                            <Navigate replace to={PathName(pathname, Customer_AddRoutesPaths.Vehicle)} />
                        )}
                    </AppointmentRoute>
                }
            />
            <Route
                path={PathName(Customer_AddRoutesPaths.Vehicle, '*')}
                element={
                    <AppointmentRoute>
                        <Vehicle
                            asWizard
                            onProceed={() => {
                                if (towInProcess) {
                                    navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.TowInAdditionalDetails));
                                }
                                else {
                                    navigate(PathName(pathname, Customer_AddRoutesPaths.Mileage));
                                }
                            }}
                        />
                    </AppointmentRoute>
                }
            />
            <Route
                path={PathName(Customer_AddRoutesPaths.Mileage, '*')}
                element={
                    <AppointmentRoute>
                        <Mileage onProceed={() => navigate(PathName(pathname, Customer_AddRoutesPaths.Tagnumber))} />
                    </AppointmentRoute>
                }
            />
            <Route
                path={PathName(Customer_AddRoutesPaths.Tagnumber, '*')}
                element={
                    <AppointmentRoute>
                        <TagNumber onProceed={props.onProceed} onSkip={props.onSkip} />
                    </AppointmentRoute>
                }
            />
            <Route
                path={''}
                element={
                    <ScrollableBody
                        heading={
                            <Heading
                                roundedIcon={
                                    <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                                        <GMSizedIcon
                                            iconType={'Star-Circle'}
                                            iconSize={'extraLarge'}
                                            ariaLabel={Localization.CircleStarIcon}
                                        />
                                    </GMRoundedIcon>
                                }
                                primaryHeading={Localization.FirstVisit}
                                secondaryHeading={Localization.GladToHaveYou}
                                showMustache
                            />
                        }
                        proceedButtons={
                            <GMButtonGroup
                                alignment={'center'}
                                buttons={[
                                    (
                                        <CTAButton
                                            styleType={'primary'}
                                            onClick={() => navigate(PathName(pathname, Customer_AddRoutesPaths.Scan))}
                                            label={Localization.ScanMyLicense}
                                            title={Localization.ClickToScanLicense}
                                            kioskOptions={{
                                                square: true,
                                                wider: currentLocale === Locales.fr,
                                            }}
                                            id={Localization.ClickToScanLicense}
                                            ariaLabel={Localization.ClickToScanLicense}
                                        />
                                    ),
                                    (
                                        <CTAButton
                                            styleType={'secondary'}
                                            onClick={() => navigate(PathName(pathname, Customer_AddRoutesPaths.CustomerInfo))}
                                            label={Localization.EnterInfoMyself}
                                            title={Localization.ClickToBeginManualInput}
                                            kioskOptions={{
                                                square: true,
                                                wider: currentLocale === Locales.fr,
                                                squareOptions: {
                                                    roundedIcon: (
                                                        <GMRoundedIcon
                                                            themeType={'primary'}
                                                            allowInvertedPrimary
                                                            roundedBackgroundSize={'large'}
                                                        >
                                                            <GMSizedIcon iconSize={'custom'} iconType={'No'} />
                                                        </GMRoundedIcon>
                                                    ),
                                                },
                                            }}
                                            ariaLabel={Localization.ClickToBeginManualInput}
                                            id={Localization.ClickToBeginManualInput}
                                        />
                                    ),
                                ]}
                            />
                        }
                        unsetButtons
                    />
                }
            />
        </Routes>
    );
};
